/**
 * AgentOptionsMenu — small popover anchored to the per-agent ⋮ button.
 * Actions: Configure / Rename / Duplicate / Delete. Click-outside + Esc
 * dismiss. Rename swaps to an inline input over the agent name row.
 *
 * Wire frames (see ~/Atmosphere/coord/PROTOCOL.md):
 *   - Configure → opens AgentWizard pre-filled (handler given by caller)
 *   - Rename    → emits {type:"agent.rename", id, name}
 *   - Duplicate → emits {type:"agent.duplicate", id} (ADR-001)
 *   - Delete    → emits {type:"agent.delete", id}
 */

import { Icon } from "./Icon";

export interface AgentOptionsOpts {
  anchor: HTMLElement;
  agentId: string;
  agentName: string;
  onConfigure: () => void;
  onRename: () => void;     // caller opens inline edit
  onDuplicate: () => void;  // caller emits agent.duplicate (ADR-001)
  onDelete: () => void;     // caller emits agent.delete + optimistically removes
}

function row(label: string, onPick: () => void, danger = false): HTMLElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "agent-pop__row" + (danger ? " agent-pop__row--danger" : "");
  b.textContent = label;
  b.addEventListener("click", (e) => {
    e.stopPropagation();
    onPick();
  });
  return b;
}

export function AgentOptionsMenu(opts: AgentOptionsOpts): HTMLElement {
  const { anchor } = opts;

  const pop = document.createElement("div");
  pop.className = "agent-pop";
  pop.setAttribute("role", "menu");
  pop.setAttribute("aria-label", `${opts.agentName} options`);

  // Position — fixed, anchored to the anchor's bottom-right corner.
  const rect = anchor.getBoundingClientRect();
  pop.style.position = "fixed";
  pop.style.top = `${Math.round(rect.bottom + 4)}px`;
  pop.style.right = `${Math.round(window.innerWidth - rect.right)}px`;

  // Head — agent name + close
  const head = document.createElement("div");
  head.className = "agent-pop__head";
  const ttl = document.createElement("span");
  ttl.className = "agent-pop__title";
  ttl.textContent = opts.agentName;
  head.appendChild(ttl);
  const close = document.createElement("button");
  close.type = "button";
  close.className = "agent-pop__close";
  close.setAttribute("aria-label", "Close");
  close.appendChild(Icon("close", 14));
  close.addEventListener("click", (e) => { e.stopPropagation(); dismiss(); });
  head.appendChild(close);
  pop.appendChild(head);

  pop.appendChild(row("Configure", () => { dismiss(); opts.onConfigure(); }));
  pop.appendChild(row("Rename", () => { dismiss(); opts.onRename(); }));
  pop.appendChild(row("Duplicate to new chat", () => { dismiss(); opts.onDuplicate(); }));
  pop.appendChild(row("Delete", () => { dismiss(); opts.onDelete(); }, true));

  function onDocClick(e: MouseEvent): void {
    if (!pop.contains(e.target as Node)) dismiss();
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") dismiss();
  }

  let dismissed = false;
  function dismiss(): void {
    if (dismissed) return;
    dismissed = true;
    document.removeEventListener("mousedown", onDocClick, true);
    document.removeEventListener("keydown", onKey);
    pop.remove();
  }

  // Defer wiring 1 frame so the click that opened the menu doesn't
  // immediately register as an outside-click and close it.
  requestAnimationFrame(() => {
    document.addEventListener("mousedown", onDocClick, true);
    document.addEventListener("keydown", onKey);
  });

  return pop;
}

export default AgentOptionsMenu;
